import { useState, useEffect } from "react";
import { getProducts, getProductById, getRelatedProducts } from "@/api/products";

/** Same-key fetches share one HTTP request (e.g. React Strict Mode double effect). */
const inflightProductLists = new Map();
const inflightProductById = new Map();
const inflightRelatedByProductId = new Map();

export const useProducts = (filters = {}) => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filterKey = JSON.stringify(filters || {});

  useEffect(() => {
    if (!filters) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        let promise = inflightProductLists.get(filterKey);
        if (!promise) {
          promise = getProducts(filters)
            .then((data) => {
              inflightProductLists.delete(filterKey);
              return data;
            })
            .catch((err) => {
              inflightProductLists.delete(filterKey);
              throw err;
            });
          inflightProductLists.set(filterKey, promise);
        }

        const data = await promise;
        if (!cancelled) {
          setProducts(data.items);
          setPagination({
            total: data.total || 0,
            pages: data.pages || 1,
            page: data.page || 1,
          });
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [filterKey]);

  return { products, loading, error, pagination };
};

export const useProduct = (id) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        let promise = inflightProductById.get(id);
        if (!promise) {
          promise = getProductById(id)
            .then((data) => {
              inflightProductById.delete(id);
              return data;
            })
            .catch((err) => {
              inflightProductById.delete(id);
              throw err;
            });
          inflightProductById.set(id, promise);
        }
        const data = await promise;
        if (!cancelled) setProduct(data);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { product, loading, error };
};

export const useRelatedProducts = (product) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!product?._id) return;

    let cancelled = false;
    const key = product._id;

    const fetchRelated = async () => {
      try {
        setLoading(true);
        setError(null);
        let promise = inflightRelatedByProductId.get(key);
        if (!promise) {
          promise = getRelatedProducts(product)
            .then((data) => {
              inflightRelatedByProductId.delete(key);
              return data;
            })
            .catch((err) => {
              inflightRelatedByProductId.delete(key);
              throw err;
            });
          inflightRelatedByProductId.set(key, promise);
        }
        const data = await promise;
        if (!cancelled) setRelatedProducts(data);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRelated();
    return () => {
      cancelled = true;
    };
  }, [product]);

  return { relatedProducts, loading, error };
};
